import { API_DOMAIN } from "@launchos/shared-utils/env";
import { getTokenInfo } from "@/api/auth";

// ADM-2 — the Admin screen's calls (/v1/admin) and the browser side of "log in as".
// While viewing an account, the staff member's own sign-in is parked under adminToken/adminUser
// and the pass sits in token/user, so every app works on the customer's account unchanged.

export type AdminAccount = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  signedUp: string | null;
  lastLogin: string | null;
  permissions: string[];
  isAdmin: boolean;
  deactivated: boolean;
  teamMemberOf: string | null;
  app: string | null;
  // SL-3: from the sign-in log
  lastActive: string | null;
  activeDays30: number;
  signIns30: number;
  failed30: number;
};

const call = async (path: string, { token, method = "GET" }: { token: string | null; method?: string }) => {
  const response = await fetch(`${API_DOMAIN}/admin${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  const data = await response.json().catch(() => null);
  // no JSON back usually means the server doesn't have the Admin routes yet
  if (data === null) throw new Error(response.status === 404 ? "The Admin screen isn't turned on for this server yet." : "Couldn't reach the server. Try again.");
  if (!response.ok || data?.error) throw new Error(data?.error || "Something went wrong. Try again.");
  return data;
};

export const getAdminAccounts = async ({ token, q = "" }: { token: string | null; q?: string }): Promise<{ accounts: AdminAccount[]; total: number }> =>
  call(`/accounts?q=${encodeURIComponent(q)}`, { token });

export type AdminVisit = { at: string; action: string; staffEmail?: string; staffId: string; accountId: string; pass?: string; added?: string[]; removed?: string[] };

export const getAccountVisits = async ({ token, accountId }: { token: string | null; accountId: string }): Promise<AdminVisit[]> =>
  call(`/accounts/${accountId}/visits`, { token });

export type Viewing = { name: string; email: string; company: string | null; until: number };

export const getViewing = (): Viewing | null => {
  try {
    if (!localStorage.getItem("adminToken")) return null;
    return JSON.parse(localStorage.getItem("adminViewing") || "null");
  } catch (e) {
    return null;
  }
};

// start viewing an account: get a pass, park our own sign-in, switch to the customer
export const loginAs = async ({ token, accountId }: { token: string | null; accountId: string }) => {
  if (!token) throw new Error("You're signed out. Sign in again.");
  const { token: pass, expiresIn, account } = await call(`/accounts/${accountId}/loginAs`, { token, method: "POST" });
  const info = await getTokenInfo(pass);
  if (!info || info.error) throw new Error("Couldn't open that account. Try again.");
  const { id, firstName, lastName, email, permissions } = info;
  localStorage.setItem("adminToken", token);
  localStorage.setItem("adminUser", localStorage.getItem("user") || "");
  localStorage.setItem("adminViewing", JSON.stringify({ name: account.name, email: account.email, company: account.company, until: Date.now() + expiresIn * 1000 }));
  localStorage.setItem("token", pass);
  localStorage.setItem("user", JSON.stringify({ id, firstName, lastName, email, permissions }));
};

// stop viewing: end the pass (if it still works) and put our own sign-in back
export const returnToMyAccount = async () => {
  const pass = localStorage.getItem("token");
  const adminToken = localStorage.getItem("adminToken");
  if (!adminToken) return;
  try {
    if (pass) await call("/loginAs", { token: pass, method: "DELETE" });
  } catch (e) {
    // an expired pass is already gone; returning still works
  }
  localStorage.setItem("token", adminToken);
  localStorage.setItem("user", localStorage.getItem("adminUser") || "");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminUser");
  localStorage.removeItem("adminViewing");
};

// ADM-6 — turn apps on or off for an account; returns the account's new permission list
export const setAccountPermissions = async ({ token, accountId, add = [], remove = [] }: { token: string | null; accountId: string; add?: string[]; remove?: string[] }): Promise<{ permissions: string[] }> => {
  const response = await fetch(`${API_DOMAIN}/admin/accounts/${accountId}/permissions`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ add, remove }),
  });
  const data = await response.json().catch(() => null);
  if (!data || data.error) throw new Error(data?.error || "Couldn't save that change. Try again.");
  return data;
};

export const getAdminMe = async ({ token }: { token: string | null }): Promise<{ isOwner: boolean }> => call("/me", { token });

// SL-3 — one account's recent sign-ins and active days
export type SigninEntry = { at: string; ok: boolean; method?: string; reason?: string | null; ip?: string | null; device?: string | null };
export const getSigninHistory = async ({ token, accountId }: { token: string | null; accountId: string }): Promise<{ activeDays: string[]; signIns: SigninEntry[] }> =>
  call(`/accounts/${accountId}/signins`, { token });
